// Where a bot goes when no fight holds it (botBrain.ts decides fights): which capture point is worth it
// right now, when to take a monster, and which enemy ground to step out of. Reads the same public state a
// player sees — points' progress, visible enemies and zones — never the fog.
import { LAYOUT } from './layout.js';
import { maxHpOf } from './artifacts.js';
import { MONSTERS, type Monster, type MonsterKind } from './monsters.js';
// Only read inside function bodies — safe across the game.ts ↔ botBrain.ts ↔ botGoals.ts import cycle.
import { RULES, type Game, type Hero, type BotState, type Point3 } from './game.js';
import type { Team } from './layout.js';

export const GOALS = {
  defendReach: 25,       // m within which a bot turns back to a point of ours being taken
  enemyNear: 4,          // m past the point's radius an enemy counts as on it
  hunt: { hp: 0.7, clearOf: 18, reach: 30, gather: 9, gatherFor: 12, abortHp: 0.45 },
};

const flatDist = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.hypot(a.x - b.x, a.z - b.z);
const hpShare = (h: Hero) => h.hp / maxHpOf(h);
const mateOf = (g: Game, h: Hero) => g.heroes.find(a => a !== h && a.team === h.team && a.alive);
const visibleEnemies = (g: Game, h: Hero) => g.heroes.filter(e => e.team !== h.team && e.alive && g.visibleTo(h.team, e));
/** Capture progress from `team`'s side: 1 = held by it, -1 = held by the other. */
const ownProgress = (p: Point3, team: Team) => (team === 'a' ? p.progress : -p.progress);

/* ---------------- points ---------------- */

/** Ours, and someone is taking it back: contested, eroded, or an enemy seen on it. */
export function pointThreatened(g: Game, h: Hero, p: Point3): boolean {
  if (p.owner !== h.team) return false;
  return p.contested || ownProgress(p, h.team) < 0.999
    || visibleEnemies(g, h).some(e => flatDist(e.pos, p.pos) < LAYOUT.pointRadius + GOALS.enemyNear);
}

/** The point `h` stands on turns ours within `secs`, uncontested. */
export function captureFinishing(g: Game, h: Hero, secs: number): boolean {
  const R = LAYOUT.pointRadius;
  const p = g.points.find(q => flatDist(q.pos, h.pos) < R);
  if (!p || p.owner === h.team || p.contested) return false;
  const ours = g.heroes.filter(a => a.team === h.team && a.alive && flatDist(a.pos, p.pos) < R).length;
  const need = (1 - ownProgress(p, h.team)) * (ours >= 2 ? RULES.captureDuo : RULES.captureSolo);
  return need <= secs;
}

const enemiesOn = (g: Game, h: Hero, p: Point3) =>
  visibleEnemies(g, h).filter(e => flatDist(e.pos, p.pos) < LAYOUT.pointRadius + GOALS.enemyNear).length;

/* The lane plan: the bot's own lane point until it's ours, then mid, then whatever the enemy still holds —
   which already keeps the two apart unless both have a point to take together. One situation changes it: one
   of our points being taken within reach, with the teammate not nearer to it (or two enemies on it) and this
   bot not in the middle of a capture of its own — then it goes back and holds it. Measured: a wider defence
   (from anywhere, or abandoning a capture) and splitting a pair that captures together both lose games. */
export function choosePoint(g: Game, h: Hero, bot: BotState): Point3 {
  const byId = (id: string) => g.points.find(p => p.id === id)!;
  const mine = (id: string) => byId(id).owner === h.team;
  const lane = bot.lane || (bot.lane = h.id % 2 === 0 ? 'B' : (h.team === 'a' ? 'A' : 'C'));
  let goal = !mine(lane) ? lane : !mine('B') ? 'B' : (g.points.find(p => !mine(p.id)) ?? { id: lane }).id;
  const mate = mateOf(g, h);
  // A teammate off shopping or hunting keeps a stale goal: it isn't holding anything.
  const mateOn = (p: Point3) => !!mate && !mate.bot?.retreat && !mate.bot?.shopping && mate.bot?.hunt == null
    && (flatDist(mate.pos, p.pos) < LAYOUT.pointRadius + 1 || mate.bot?.goal === p.id);
  const capturing = g.points.some(p => p.owner !== h.team && flatDist(p.pos, h.pos) < LAYOUT.pointRadius);
  const defend = capturing ? undefined : g.points.filter(p => pointThreatened(g, h, p) && flatDist(p.pos, h.pos) < GOALS.defendReach
    && (!mate || !mateOn(p) || enemiesOn(g, h, p) >= 2 || flatDist(mate.pos, p.pos) > flatDist(h.pos, p.pos)))
    .sort((x, y) => flatDist(x.pos, h.pos) - flatDist(y.pos, h.pos))[0];
  if (defend) goal = defend.id;
  bot.goal = goal;
  return byId(goal);
}

/* ---------------- hazards ---------------- */

export type Hazard = { x: number; z: number; r: number };

/** Enemy ground in sight that hurts or hampers: damaging, slowing or heal-cutting pools (not the ones glued to
    a hero — those move with the fight) and Тотем Гнева. */
export function hazardsFor(g: Game, h: Hero): Hazard[] {
  const out: Hazard[] = [];
  for (const z of g.pools) {
    if (z.team === h.team || z.follow || z.until <= g.t || !(z.dmg || z.slow || z.burn || z.healReduction)) continue;
    if (g.inSight(h.team, z.pos)) out.push({ x: z.pos.x, z: z.pos.z, r: z.r });
  }
  for (const t of g.totems) if (t.team !== h.team && t.kind === 'wrath' && g.totemVisibleTo(h.team, t)) out.push({ x: t.pos.x, z: t.pos.z, r: t.r });
  return out;
}
export const inHazard = (hz: Hazard[], p: { x: number; z: number }) => hz.some(z => Math.hypot(p.x - z.x, p.z - z.z) < z.r);

/** The nearest walkable spot just outside every hazard around `from`, or null when none within reach. */
export function hazardExit(g: Game, hz: Hazard[], from: { x: number; z: number }): { x: number; z: number } | null {
  let best: { x: number; z: number } | null = null, bestD = Infinity;
  for (let i = 0; i < 16; i++) {
    const a = i * Math.PI / 8;
    for (let d = 1; d <= 8; d += 1) {
      const p = { x: from.x + Math.sin(a) * d, z: from.z + Math.cos(a) * d };
      if (inHazard(hz, p)) continue;
      if (g.nav.isWalkable(p.x, p.z) && d < bestD) { best = p; bestD = d; }
      break;
    }
  }
  return best;
}

/** `dest`, or — when it lies in a hazard — the nearest ground just outside it on `from`'s side, so a bot
    walking there stops at the edge and stays (a spot exactly on the edge would flip in and out). */
export function safeGround(g: Game, hz: Hazard[], dest: { x: number; z: number }, from: { x: number; z: number }): { x: number; z: number } {
  let p = dest;
  for (let pass = 0; pass < 3; pass++) {
    const z = hz.find(q => Math.hypot(p.x - q.x, p.z - q.z) < q.r + SAFE_MARGIN);
    if (!z) break;
    let dx = from.x - z.x, dz = from.z - z.z, len = Math.hypot(dx, dz);
    if (len < 1e-3) { dx = 1; dz = 0; len = 1; }
    p = { x: z.x + dx / len * (z.r + SAFE_MARGIN), z: z.z + dz / len * (z.r + SAFE_MARGIN) };
  }
  return g.nav.isWalkable(p.x, p.z) && !inHazard(hz, p) ? p : hazardExit(g, hz, dest) ?? dest;
}
const SAFE_MARGIN = 0.6;

/** Walking the nav path from `from` toward `to` until within `reach` of it would cross a hazard (a straight
    line when there's no path to follow). Only worth calling with hazards about — it pathfinds. */
export function approachCrosses(g: Game, hz: Hazard[], from: { x: number; z: number }, to: { x: number; z: number }, reach: number): boolean {
  if (Math.hypot(to.x - from.x, to.z - from.z) <= reach) return false;
  const path = g.nav.findPath(from.x, from.z, to.x, to.z);
  const line = [from, ...(path.length ? path : [to])];
  for (let i = 1; i < line.length; i++) {
    const a = line[i - 1]!, b = line[i]!, len = Math.hypot(b.x - a.x, b.z - a.z);
    for (let s = Math.min(0.5, len); s <= len; s += 0.5) {
      const p = { x: a.x + (b.x - a.x) * s / len, z: a.z + (b.z - a.z) * s / len };
      if (Math.hypot(to.x - p.x, to.z - p.z) <= reach) return false;
      if (inHazard(hz, p)) return true;
    }
  }
  return false;
}

/* ---------------- monsters ---------------- */

/* A monster's buff as a share of base hp: a fight goes roughly as hp × damage rate, so +x% attack speed is
   worth about +x% hp, and running speed counts the same — here reaching a point first is what wins it. */
export const buffWorth = (kind: MonsterKind) => {
  const b = MONSTERS[kind].buff;
  return b.hpPct + b.atkSpeed + b.moveSpeed;
};

/* Worth a detour: our team lacks its buff, both of us are healthy and near its den, nobody from the other side
   in sight, none of our points is being taken meanwhile, and the two of us can take its blows for as long as
   the kill lasts without ending up too low to fight — the buff's worth counts as hp we can spare (Бегемот
   hands it straight back, Грифон's speed wins as much in the fights after). The teammate must be a free bot
   (it runs this same check and comes along), already on this hunt, or already on the monster. The more
   valuable buff first, then the nearer den. Measured, a monster costs both bots half a minute off the
   points, so this stays deliberately rare. */
export function monsterToHunt(g: Game, h: Hero): Monster | null {
  const H = GOALS.hunt, mate = mateOf(g, h);
  if (!mate || hpShare(h) < H.hp || hpShare(mate) < H.hp) return null;
  if (g.heroes.some(e => e.team !== h.team && e.alive && g.visibleTo(h.team, e) && Math.min(flatDist(e.pos, h.pos), flatDist(e.pos, mate.pos)) < H.clearOf)) return null;
  if (g.points.some(p => pointThreatened(g, h, p))) return null;
  const mateFree = !!mate.bot && !mate.bot.retreat && !mate.bot.shopping && mate.order?.type !== 'attack';
  const mateIn = (m: Monster) => (mate.bot?.hunt === m.id || mateFree || (mate.order?.type === 'attack' && mate.order.target === m));
  const ok = g.monsters.filter(m => m.alive && m.state !== 'return' && g.teamBuffs[h.team][m.kind] === null
    && (!m.target || m.target.team === h.team)
    && flatDist(m.den, h.pos) < H.reach && flatDist(m.den, mate.pos) < H.reach && mateIn(m));
  const budget = (m: Monster) => {
    // Its swings over the time the two of us need to kill it, against what we can spare.
    const dps = [h, mate].reduce((s, x) => s + x.def.dmg / g.attackCdOf(x), 0);
    const takes = m.hp / dps * MONSTERS[m.kind].dmg / MONSTERS[m.kind].attackCd;
    const spare = h.hp + mate.hp - 0.35 * (maxHpOf(h) + maxHpOf(mate));
    return takes <= spare + buffWorth(m.kind) * (h.def.hp + mate.def.hp);
  };
  return ok.filter(budget)
    .sort((x, y) => buffWorth(y.kind) - buffWorth(x.kind) || flatDist(x.den, h.pos) - flatDist(y.den, h.pos))[0] ?? null;
}

/** Keep at it only while nothing changed for the worse: an enemy in sight or low hp ends the hunt. */
export function huntStillOn(g: Game, h: Hero, m: Monster, since: number): boolean {
  const H = GOALS.hunt, mate = mateOf(g, h);
  if (!m.alive || m.state === 'return' || hpShare(h) < H.abortHp || !mate) return false;
  // The teammate never turned up: give up rather than wait at the den.
  if (!(m.target && m.target.team === h.team) && g.t - since > H.gatherFor) return false;
  return !g.heroes.some(e => e.team !== h.team && e.alive && g.visibleTo(h.team, e) && flatDist(e.pos, m.pos) < H.clearOf);
}
